import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { Text } from '@/components/Text';
import { PremiumLimit } from '@/partials/dashboard/Cards/UpdateCard/PremiumLimit';
import { useAccessoryStore } from '@/stores/useAccessoryStore';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/formatDateTime';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSadTear } from 'react-icons/fa';
import { FaNfcSymbol } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';

export const UpdateCardAccessories = () => {
  const { slug } = useParams();
  const {
    accessories,
    isLoading,
    listAccessories,
    filters,
    setFilters,
    limitExceeded,
  } = useAccessoryStore();
  const { t } = useTranslation();
  useEffect(() => {
    if (!slug) return;

    listAccessories({
      card_slug: slug,
      current_page: filters.current_page || 1,
      per_page: filters.per_page || 10,
      sort_field: filters.sort_field || 'created_at',
      sort_order: filters.sort_order || 'desc',
    });
  }, [
    slug,
    listAccessories,
    filters.current_page,
    filters.per_page,
    filters.sort_field,
    filters.sort_order,
  ]);

  const fetchMoreData = () => {
    if (!isLoading && (filters.current_page || 1) < (filters.last_page || 1)) {
      setFilters({
        current_page: (filters.current_page || 1) + 1,
      });
    }
  };

  if (accessories.length === 0 && !isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center p-4 text-center"
      >
        <div className="mb-6 text-6xl text-linko-light">
          <FaSadTear />
        </div>
        <Text className="mb-4 text-xl font-semibold text-gray-800">
          {t('updateCard:accessories.empty.title')}
        </Text>
        <div className="mb-8 max-w-2xl space-y-8 text-gray-600">
          <Text>{t('updateCard:accessories.empty.description.main')}</Text>
          <div>
            <Text className="mb-4 font-semibold">
              {t('updateCard:accessories.empty.description.options')}
            </Text>
            <div className="flex flex-col gap-2">
              <Text className="text-linko-light">
                {t('updateCard:accessories.empty.description.option1')}
              </Text>
              <Text className="text-linko-light">
                {t('updateCard:accessories.empty.description.option2')}
              </Text>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Button
            className="bg-linko-light hover:bg-linko-light/90"
            onClick={() => {
              window.open(
                'https://linko-connect.com/produit/linko-light/',
                '_blank',
              );
            }}
          >
            {t('updateCard:accessories.empty.cta.standard')}
          </Button>
          <Button
            className="bg-linko-light hover:bg-linko-light/90"
            onClick={() => {
              window.open('https://linko-connect.com/contact/', '_blank');
            }}
          >
            {t('updateCard:accessories.empty.cta.custom')}
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      {limitExceeded && (
        <PremiumLimit
          text={t('updateCard:accessories.limit.limit_exceeded')}
          ctaText={t('updateCard:accessories.limit.limit_exceeded_cta')}
        />
      )}
      <div
        id="scrollableDiv"
        className="h-full space-y-6 overflow-y-auto pt-4 md:pt-0"
      >
        <InfiniteScrollList
          dataLength={accessories.length}
          next={fetchMoreData}
          hasMore={(filters.current_page || 1) < (filters.last_page || 1)}
          total={accessories.length}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {accessories.map(accessory => (
              <Card
                key={accessory.uuid}
                className="aspect-[1.6] overflow-hidden rounded-lg bg-gradient-to-br from-linko-light/10 to-linko-light/5 p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex h-full flex-col justify-between">
                  <div>
                    {/* Point clignotant */}
                    <div className="relative mb-4 flex items-center justify-between">
                      <div
                        className={cn(
                          'absolute right-0 top-1/2 size-2 -translate-y-1/2 animate-pulse rounded-full',
                          accessory.status === 'linked'
                            ? 'bg-green-500'
                            : 'bg-red-500',
                        )}
                      />
                      <span className="text-sm font-medium text-linko-light">
                        {accessory.owner_type === 'user'
                          ? t('updateCard:accessories.card.personal')
                          : t('updateCard:accessories.card.company')}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          accessory.status === 'linked'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {t(
                          `updateCard:accessories.card.status.${
                            accessory.status === 'linked'
                              ? 'linked'
                              : 'unlinked'
                          }`,
                        )}
                      </span>
                    </div>

                    <div className="mb-2">
                      <span className="text-xs text-gray-500">
                        {t('updateCard:accessories.card.id')}
                      </span>
                      <p className="font-mono text-sm text-gray-800">
                        {accessory.uuid}
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        {t('updateCard:accessories.card.created')}{' '}
                        {formatDateTime(accessory.created_at)}
                      </span>
                      <div className="flex items-center gap-1">
                        <FaNfcSymbol className="text-linko-light" />
                        <span>NFC</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </InfiniteScrollList>
      </div>
    </>
  );
};
